/**
 * Web push/notification hook.
 *
 * Keep this file free of `expo-notifications` imports. The native module has
 * a web auto-registration side effect that logs a warning even when we never
 * request an Expo push token.
 */
import { useEffect } from 'react'
import { useRouter } from 'expo-router'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from './use-auth'
import { supabase } from '../supabase'
import { toast } from '../toast'
import {
  isSafeInternalPath,
  resolveNotificationRoute,
  shouldShowForegroundNotificationToast,
} from '../utils/notification-routes'
import { notificationKeys } from './use-notifications-inbox'

const WEB_PUSH_VAPID_PUBLIC_KEY =
  typeof process !== 'undefined' ? (process.env.EXPO_PUBLIC_WEB_PUSH_VAPID_PUBLIC_KEY ?? '') : ''

function base64UrlToUint8Array(value: string): Uint8Array {
  const padding = '='.repeat((4 - (value.length % 4)) % 4)
  const raw = window.atob((value + padding).replace(/-/g, '+').replace(/_/g, '/'))
  return Uint8Array.from(raw, (char) => char.charCodeAt(0))
}

async function registerWebPushSubscription(userId: string): Promise<void> {
  if (!WEB_PUSH_VAPID_PUBLIC_KEY) return
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return

  if (!(await requestWebNotificationPermission())) return

  const registration = await navigator.serviceWorker.register('/push-sw.js', { scope: '/' })
  const existing = await registration.pushManager.getSubscription()
  const subscription =
    existing ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: base64UrlToUint8Array(WEB_PUSH_VAPID_PUBLIC_KEY).buffer as ArrayBuffer,
    }))

  const { error } = await supabase.from('push_tokens').upsert(
    {
      profile_id: userId,
      token: JSON.stringify(subscription.toJSON()),
      provider: 'web',
      device_type: 'web',
      device_name: navigator.userAgent.slice(0, 160),
      is_active: true,
      failure_count: 0,
      last_used_at: new Date().toISOString(),
    },
    { onConflict: 'token' },
  )

  if (error && __DEV__) console.warn('[Push] Web subscription could not be saved:', error.message)
}

async function requestWebNotificationPermission(): Promise<boolean> {
  if (typeof window === 'undefined' || !('Notification' in window)) return false
  if (Notification.permission === 'granted') return true
  if (Notification.permission === 'denied') return false
  const result = await Notification.requestPermission()
  return result === 'granted'
}

function showWebNotification(
  title: string,
  body: string,
  path?: string,
  navigate?: (path: string) => void,
) {
  if (typeof window === 'undefined' || !('Notification' in window)) return
  if (Notification.permission !== 'granted') return

  const notification = new Notification(title, {
    body,
    icon: '/assets/images/icon.png',
  })

  if (path && isSafeInternalPath(path)) {
    notification.onclick = () => {
      window.focus()
      notification.close()
      navigate?.(path)
    }
  }
}

export function usePushNotifications() {
  const { user } = useAuth()
  const router = useRouter()
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!user?.id) return

    void registerWebPushSubscription(user.id).catch((error) => {
      if (__DEV__) console.warn('[Push] Web subscription unavailable:', error)
    })

    const channel = supabase
      .channel(`notifications:${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `profile_id=eq.${user.id}`,
        },
        (payload) => {
          const n = payload.new as {
            id: string
            title: string | null
            body: string | null
            data: unknown
          }
          const safeScreen = resolveNotificationRoute(n.data) ?? undefined

          // Bookkeeping FIRST, decoration second. `new Notification(...)`
          // throws on Android Chrome — the constructor is unsupported there —
          // and with the visible surfaces running first, that single throw
          // killed the inbox and badge refresh below it. The badge must never
          // depend on the fancier surfaces surviving.
          queryClient.invalidateQueries({ queryKey: notificationKeys.inbox(user.id) })
          queryClient.invalidateQueries({ queryKey: notificationKeys.unread(user.id) })

          try {
            showWebNotification(n.title ?? 'Tago', n.body ?? '', safeScreen, (path) => {
              router.push(path as never)
            })
            if (shouldShowForegroundNotificationToast(n.data, window.location.pathname)) {
              toast.info(n.body ?? n.title ?? '', {
                action: safeScreen
                  ? {
                      label: 'Abrir',
                      onPress: () => router.push(safeScreen as never),
                    }
                  : undefined,
              })
            }
          } catch {
            // The row is in the inbox and the badge is fresh; losing the toast
            // is cosmetic. Crashing the subscription over it was not.
          }
        },
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [user?.id, queryClient, router])
}
